/**
 * Widget điều khiển đọc văn bản ngụy trang (TTS Floating Widget)
 * Tính năng cao cấp:
 * 1. Mở rộng thông minh (Smart Viewport Bounds): Tự động tính toán vị trí để không bao giờ bị mép màn hình che khuất.
 * 2. Hiệu ứng Morphing Animation siêu mượt 60fps (chuẩn Dynamic Island / Spring Curve) khi mở rộng và thu nhỏ.
 * 3. Kéo thả tự do (Draggable) trên cả Desktop (chuột) và Mobile (cảm ứng chạm), ghi nhớ vị trí.
 * 4. Tự động thu nhỏ (Auto-minimize) sau 4s khi đang phát mà không thao tác.
 * 5. Nhấp vào icon Loa thu nhỏ để bung mở đầy đủ thanh điều khiển.
 */
import { TTS_EVENTS, TTS_STATES } from '../constants/ttsEvents.js';

export class TTSControlBar {
  /**
   * @param {import('../ttsEngine.js').TTSEngine} ttsEngine
   */
  constructor(ttsEngine) {
    this.tts = ttsEngine;
    this.containerEl = null;

    this.btnPlay = null;
    this.btnPrev = null;
    this.btnNext = null;
    this.voiceSelect = null;
    this.speedSlider = null;
    this.speedVal = null;
    this.statusText = null;
    this.speakerIcon = null;
    this.expandedContent = null;

    // Trạng thái thu nhỏ & Hẹn giờ tự động thu gọn
    this.isMinimized = false;
    this.autoMinimizeTimer = null;
    this.autoMinimizeDelay = 4000;
    this.isHovered = false;

    // Biến phục vụ kéo thả (Drag and drop)
    this.isDragging = false;
    this.hasMoved = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.elementStartX = 0;
    this.elementStartY = 0;

    this.createDom();
    this.bindEvents();
    this.bindDragEvents();
    this.restorePreferences();
    this.restorePosition();
  }

  createDom() {
    if (document.getElementById('tts-floating-widget')) {
      this.containerEl = document.getElementById('tts-floating-widget');
      return;
    }

    const widget = document.createElement('div');
    widget.id = 'tts-floating-widget';
    widget.className = 'tts-floating-widget';
    widget.style.display = 'none';

    widget.innerHTML = `
      <div class="tts-widget-inner" id="tts-widget-inner">
        <!-- Nút biểu tượng trạng thái loa (Luôn hiện diện) -->
        <div class="tts-speaker-icon" id="tts-speaker-indicator" title="Nhấp để thu gọn / mở rộng trợ lý đọc">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        </div>

        <!-- Khối nội dung điều khiển mở rộng (Animated Morphing Container) -->
        <div class="tts-expanded-content" id="tts-expanded-content">
          <!-- Trạng thái vị trí đọc -->
          <div class="tts-status-col" id="tts-status-col">
            <div class="tts-status-title" id="tts-status-title" style="display:flex;align-items:center;">
              <span>Trợ lý đọc</span>
              <span id="tts-source-badge" style="font-size:10px;padding:1px 6px;border-radius:8px;background:#e6f4ea;color:#137333;margin-left:6px;font-weight:600;">100% FE</span>
            </div>
            <div class="tts-status-sub" id="tts-status-sub">Sẵn sàng</div>
          </div>

          <!-- Cụm nút điều hướng đoạn/câu -->
          <div class="tts-controls-group">
            <button class="tts-btn-icon" id="tts-btn-prev" title="Đoạn trước (Alt+P, [)">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
            </button>

            <button class="tts-btn-play" id="tts-btn-play" title="Phát / Tạm dừng (Alt+Space)">
              <svg id="tts-icon-play" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              <svg id="tts-icon-pause" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style="display:none;"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
            </button>

            <button class="tts-btn-icon" id="tts-btn-next" title="Đoạn tiếp theo (Alt+N, ])">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
            </button>
          </div>

          <!-- Dropdown chọn giọng đọc -->
          <div class="tts-voice-wrapper" title="Chọn giọng đọc">
            <select id="tts-voice-select" class="tts-voice-select">
              <option value="native-auto" selected>💻 Giọng Thiết Bị (Tự động tốt nhất)</option>
              <option value="ai-female">🌟 Giọng AI Nữ</option>
              <option value="ai-male">🌟 Giọng AI Nam</option>
            </select>
          </div>

          <!-- Thanh trượt kéo điều chỉnh tốc độ đọc -->
          <div class="tts-speed-slider-wrap" title="Kéo để điều chỉnh tốc độ đọc">
            <span class="tts-speed-icon">⚡</span>
            <input type="range" id="tts-speed-slider" class="tts-speed-slider" min="0.5" max="2.0" step="0.1" value="1.0" />
            <span class="tts-speed-val" id="tts-speed-val">1.0x</span>
          </div>

          <!-- Nút thu nhỏ widget -->
          <button class="tts-btn-minimize" id="tts-btn-minimize" title="Thu nhỏ thành icon loa">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M19 13H5v-2h14v2z"/>
            </svg>
          </button>

          <!-- Nút tắt / đóng hẳn widget -->
          <button class="tts-btn-close" id="tts-btn-close" title="Ẩn trợ lý (Esc)">
            &times;
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(widget);

    this.containerEl = widget;
    this.btnPlay = widget.querySelector('#tts-btn-play');
    this.btnPrev = widget.querySelector('#tts-btn-prev');
    this.btnNext = widget.querySelector('#tts-btn-next');
    this.voiceSelect = widget.querySelector('#tts-voice-select');
    this.speedSlider = widget.querySelector('#tts-speed-slider');
    this.speedVal = widget.querySelector('#tts-speed-val');
    this.statusText = widget.querySelector('#tts-status-sub');
    this.iconPlay = widget.querySelector('#tts-icon-play');
    this.iconPause = widget.querySelector('#tts-icon-pause');
    this.btnClose = widget.querySelector('#tts-btn-close');
    this.btnMinimize = widget.querySelector('#tts-btn-minimize');
    this.speakerIcon = widget.querySelector('#tts-speaker-indicator');
    this.expandedContent = widget.querySelector('#tts-expanded-content');

    this.populateVoices();

    // Tự động kiểm tra mép màn hình khi thay đổi kích thước cửa sổ
    window.addEventListener('resize', () => {
      if (!this.isMinimized && this.containerEl && this.containerEl.style.display !== 'none') {
        this.adjustPositionWithinScreen();
      }
    });
  }

  /**
   * Nạp danh sách giọng đọc khả dụng vào dropdown
   */
  updateSourceBadge() {
    const badge = document.getElementById('tts-source-badge');
    if (!badge) return;
    if (this.tts.isNativeMode()) {
      badge.textContent = '100% FE';
      badge.style.background = '#e6f4ea';
      badge.style.color = '#137333';
      badge.title = 'Phát trực tiếp trên máy của bạn (0 request mạng)';
    } else {
      badge.textContent = 'Cloud AI';
      badge.style.background = '#e8f0fe';
      badge.style.color = '#1a73e8';
      badge.title = 'Phát từ Đám mây Serverless';
    }
  }

  populateVoices() {
    if (!this.voiceSelect) return;
    const currentVal = this.voiceSelect.value || localStorage.getItem('fake_excel_tts_voice') || 'native-auto';
    const groups = this.tts.getAvailableVoiceGroups();
    this.voiceSelect.innerHTML = '';

    // Nhóm 1: Giọng Thiết Bị (100% Frontend Native - 0 request máy chủ)
    if (groups.native && groups.native.length > 0) {
      const nativeGroup = document.createElement('optgroup');
      nativeGroup.label = '💻 Giọng Thiết Bị (100% Offline FE)';
      groups.native.forEach(v => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.name;
        nativeGroup.appendChild(opt);
      });
      this.voiceSelect.appendChild(nativeGroup);
    }

    // Nhóm 2: Giọng Đám Mây AI (Cloud API)
    if (groups.cloud && groups.cloud.length > 0) {
      const cloudGroup = document.createElement('optgroup');
      cloudGroup.label = '🌟 Giọng Đám Mây (AI Cloud)';
      groups.cloud.forEach(v => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = v.name;
        cloudGroup.appendChild(opt);
      });
      this.voiceSelect.appendChild(cloudGroup);
    }

    // Khôi phục giá trị đã chọn
    if (currentVal) {
      this.voiceSelect.value = currentVal;
    }
    this.updateSourceBadge();
  }

  /**
   * Phục hồi cài đặt giọng và tốc độ từ LocalStorage
   */
  restorePreferences() {
    try {
      const savedSpeed = localStorage.getItem('fake_excel_tts_speed');
      if (savedSpeed) {
        const speed = parseFloat(savedSpeed);
        if (speed >= 0.5 && speed <= 2.5) {
          this.speedSlider.value = speed;
          this.speedVal.textContent = `${speed.toFixed(1)}x`;
          this.tts.setSpeed(speed);
        }
      }

      // Mặc định ban đầu: Ưu tiên 100% Giọng Thiết Bị (native-auto)
      const savedVoice = localStorage.getItem('fake_excel_tts_voice') || 'native-auto';
      if (savedVoice) {
        this.voiceSelect.value = savedVoice;
        this.tts.setVoice(savedVoice);
        this.updateSourceBadge();
      }
    } catch (_) {}
  }

  /**
   * Phục hồi vị trí người dùng đã kéo trước đó
   */
  restorePosition() {
    try {
      const savedPos = localStorage.getItem('fake_excel_tts_widget_pos');
      if (savedPos && this.containerEl) {
        const pos = JSON.parse(savedPos);
        if (typeof pos.left === 'number' && typeof pos.top === 'number') {
          this.containerEl.style.left = `${pos.left}px`;
          this.containerEl.style.top = `${pos.top}px`;
          this.containerEl.style.bottom = 'auto';
          this.containerEl.style.right = 'auto';

          this.adjustPositionWithinScreen();
        }
      }
    } catch (_) {}
  }

  /**
   * Tự động kiểm tra và điều chỉnh vị trí để đảm bảo widget không bao giờ bị mép màn hình che khuất
   */
  adjustPositionWithinScreen() {
    const el = this.containerEl;
    if (!el || el.style.display === 'none') return;

    // Lấy kích thước khung nhìn màn hình
    const winW = window.innerWidth;
    const winH = window.innerHeight;
    const margin = 14;

    // Lấy tọa độ và kích thước hiện tại
    const rect = el.getBoundingClientRect();
    const currentLeft = rect.left;
    const currentTop = rect.top;

    // Ước tính kích thước khi mở rộng (khoảng 490px trên desktop, 340px trên mobile)
    let width = el.offsetWidth;
    if (this.isMinimized || width < 150) {
      width = winW <= 768 ? Math.min(340, winW - 28) : 510;
    }
    const height = Math.max(50, el.offsetHeight);

    let targetLeft = currentLeft;
    let targetTop = currentTop;

    // Nếu bị tràn mép phải màn hình -> dịch sang trái
    if (targetLeft + width > winW - margin) {
      targetLeft = winW - width - margin;
    }
    // Nếu bị tràn mép trái màn hình -> kéo sang phải
    if (targetLeft < margin) {
      targetLeft = margin;
    }
    // Nếu bị tràn mép dưới màn hình -> đẩy lên trên
    if (targetTop + height > winH - margin) {
      targetTop = winH - height - margin;
    }
    // Nếu bị tràn mép trên màn hình -> đẩy xuống dưới
    if (targetTop < margin) {
      targetTop = margin;
    }

    // Áp dụng tọa độ an toàn
    if (Math.abs(targetLeft - currentLeft) > 1 || Math.abs(targetTop - currentTop) > 1) {
      el.style.left = `${targetLeft}px`;
      el.style.top = `${targetTop}px`;
      el.style.bottom = 'auto';
      el.style.right = 'auto';

      try {
        localStorage.setItem('fake_excel_tts_widget_pos', JSON.stringify({
          left: Math.round(targetLeft),
          top: Math.round(targetTop),
        }));
      } catch (_) {}
    }
  }

  /**
   * Khởi tạo cơ chế kéo thả tự do (Draggable) trên Desktop & Mobile
   */
  bindDragEvents() {
    const el = this.containerEl;
    if (!el) return;

    const onStart = (clientX, clientY, target) => {
      if (!this.isMinimized) {
        const isInteractive = target.closest('button, input, select, option');
        if (isInteractive && !target.closest('#tts-speaker-indicator')) {
          return;
        }
      }

      this.isDragging = true;
      this.hasMoved = false;
      this.dragStartX = clientX;
      this.dragStartY = clientY;

      const rect = el.getBoundingClientRect();
      this.elementStartX = rect.left;
      this.elementStartY = rect.top;

      el.classList.add('dragging');
    };

    const onMove = (clientX, clientY, e) => {
      if (!this.isDragging) return;

      const dx = clientX - this.dragStartX;
      const dy = clientY - this.dragStartY;

      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        this.hasMoved = true;
        if (e && e.cancelable) e.preventDefault();
      }

      if (this.hasMoved) {
        const rect = el.getBoundingClientRect();
        const maxLeft = window.innerWidth - rect.width - 8;
        const maxTop = window.innerHeight - rect.height - 8;

        const newLeft = Math.max(8, Math.min(maxLeft, this.elementStartX + dx));
        const newTop = Math.max(8, Math.min(maxTop, this.elementStartY + dy));

        el.style.left = `${newLeft}px`;
        el.style.top = `${newTop}px`;
        el.style.bottom = 'auto';
        el.style.right = 'auto';
      }
    };

    const onEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      el.classList.remove('dragging');

      if (this.hasMoved) {
        const rect = el.getBoundingClientRect();
        try {
          localStorage.setItem('fake_excel_tts_widget_pos', JSON.stringify({
            left: Math.round(rect.left),
            top: Math.round(rect.top),
          }));
        } catch (_) {}
      }
    };

    // 1. Chuột trên Desktop
    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      onStart(e.clientX, e.clientY, e.target);

      const mouseMoveHandler = (ev) => onMove(ev.clientX, ev.clientY, ev);
      const mouseUpHandler = () => {
        onEnd();
        window.removeEventListener('mousemove', mouseMoveHandler);
        window.removeEventListener('mouseup', mouseUpHandler);
      };

      window.addEventListener('mousemove', mouseMoveHandler);
      window.addEventListener('mouseup', mouseUpHandler);
    });

    // 2. Cảm ứng trên Mobile/Tablet
    el.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        onStart(touch.clientX, touch.clientY, e.target);
      }
    }, { passive: true });

    el.addEventListener('touchmove', (e) => {
      if (this.isDragging && e.touches.length === 1) {
        const touch = e.touches[0];
        onMove(touch.clientX, touch.clientY, e);
      }
    }, { passive: false });

    el.addEventListener('touchend', () => onEnd());
    el.addEventListener('touchcancel', () => onEnd());
  }

  /**
   * Thu nhỏ widget thành biểu tượng chiếc Loa nổi đang phát
   */
  minimize() {
    if (this.isMinimized || !this.containerEl) return;
    this.isMinimized = true;
    this.containerEl.classList.add('minimized');
    this.clearAutoMinimizeTimer();
    console.log('[TTSControlBar] Đã thu nhỏ thành icon loa mượt mà.');
  }

  /**
   * Mở rộng widget hiển thị đầy đủ các nút điều khiển, đảm bảo không bị mép màn hình che khuất
   */
  expand() {
    if (!this.isMinimized || !this.containerEl) return;
    this.isMinimized = false;

    // 1. Điều chỉnh vị trí an toàn trước và trong khi mở rộng
    this.adjustPositionWithinScreen();

    // 2. Kích hoạt animation bung mở mượt mà
    this.containerEl.classList.remove('minimized');

    // 3. Đo đạc lại một lần nữa sau khi hiệu ứng mở rộng hoàn tất
    setTimeout(() => {
      this.adjustPositionWithinScreen();
    }, 120);

    if (this.tts.isPlaying) {
      this.startAutoMinimizeTimer();
    }
    console.log('[TTSControlBar] Đã mở rộng thanh điều khiển an toàn, không bị che khuất.');
  }

  /**
   * Bắt đầu đếm ngược tự động thu nhỏ nếu người dùng không thao tác
   */
  startAutoMinimizeTimer() {
    this.clearAutoMinimizeTimer();
    if (!this.tts.isPlaying || this.isMinimized || this.isHovered) return;

    this.autoMinimizeTimer = setTimeout(() => {
      if (this.tts.isPlaying && !this.isHovered && !this.isDragging) {
        this.minimize();
      }
    }, this.autoMinimizeDelay);
  }

  clearAutoMinimizeTimer() {
    if (this.autoMinimizeTimer) {
      clearTimeout(this.autoMinimizeTimer);
      this.autoMinimizeTimer = null;
    }
  }

  bindEvents() {
    // 1. Nhấp vào icon Loa: Chuyển đổi giữa Thu nhỏ & Mở rộng
    if (this.speakerIcon) {
      this.speakerIcon.addEventListener('click', (e) => {
        if (this.hasMoved) return;
        if (this.isMinimized) {
          this.expand();
        } else {
          this.minimize();
        }
      });
    }

    // 2. Nhấp vào nút thu nhỏ thủ công
    if (this.btnMinimize) {
      this.btnMinimize.addEventListener('click', (e) => {
        e.stopPropagation();
        this.minimize();
      });
    }

    // 3. Tương tác rê chuột (Hover) để tạm dừng timer tự động thu nhỏ
    if (this.containerEl) {
      this.containerEl.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this.clearAutoMinimizeTimer();
      });

      this.containerEl.addEventListener('mouseleave', () => {
        this.isHovered = false;
        if (this.tts.isPlaying && !this.isMinimized) {
          this.startAutoMinimizeTimer();
        }
      });
    }

    // 4. Nút Play / Pause
    if (this.btnPlay) {
      this.btnPlay.addEventListener('click', () => {
        if (this.tts.state === TTS_STATES.PLAYING) {
          this.tts.pause();
        } else if (this.tts.state === TTS_STATES.PAUSED) {
          this.tts.resume();
        } else {
          this.tts.play();
        }
      });
    }

    // 5. Nút Prev / Next
    if (this.btnPrev) {
      this.btnPrev.addEventListener('click', () => {
        this.tts.prev();
        this.startAutoMinimizeTimer();
      });
    }
    if (this.btnNext) {
      this.btnNext.addEventListener('click', () => {
        this.tts.next();
        this.startAutoMinimizeTimer();
      });
    }

    // 6. Thay đổi giọng đọc
    if (this.voiceSelect) {
      this.voiceSelect.addEventListener('change', (e) => {
        const voiceId = e.target.value;
        this.tts.setVoice(voiceId);
        this.updateSourceBadge();
        try { localStorage.setItem('fake_excel_tts_voice', voiceId); } catch (_) {}
        this.startAutoMinimizeTimer();
      });
    }

    // 7. Thanh trượt kéo tốc độ đọc (Realtime Slider)
    if (this.speedSlider) {
      this.speedSlider.addEventListener('input', (e) => {
        const speed = parseFloat(e.target.value) || 1.0;
        if (this.speedVal) {
          this.speedVal.textContent = `${speed.toFixed(1)}x`;
        }
        this.tts.setSpeed(speed);
        try { localStorage.setItem('fake_excel_tts_speed', speed.toString()); } catch (_) {}
        this.startAutoMinimizeTimer();
      });
    }

    // 8. Đóng / ẩn hoàn toàn widget
    if (this.btnClose) {
      this.btnClose.addEventListener('click', (e) => {
        e.stopPropagation();
        this.tts.stop();
        this.hide();
      });
    }

    // 9. Lắng nghe các sự kiện từ TTSEngine để cập nhật UI & tự động thu nhỏ
    this.tts.on(TTS_EVENTS.PLAYBACK_STARTED, () => {
      this.setPlayingState(true);
      this.startAutoMinimizeTimer();
    });

    this.tts.on(TTS_EVENTS.PLAYBACK_RESUMED, () => {
      this.setPlayingState(true);
      this.startAutoMinimizeTimer();
    });

    this.tts.on(TTS_EVENTS.PLAYBACK_PAUSED, () => {
      this.setPlayingState(false);
      this.clearAutoMinimizeTimer();
      this.expand();
    });

    this.tts.on(TTS_EVENTS.STOPPED, () => {
      this.setPlayingState(false);
      this.clearAutoMinimizeTimer();
      this.expand();
    });

    this.tts.on(TTS_EVENTS.COMPLETED, () => {
      this.setPlayingState(false);
      this.clearAutoMinimizeTimer();
      this.expand();
      if (this.statusText) this.statusText.textContent = 'Đã hoàn tất tài liệu!';
    });

    this.tts.on(TTS_EVENTS.SEGMENT_CHANGED, (data) => {
      if (this.statusText) {
        this.statusText.textContent = `Dòng ${data.index + 1} / ${data.total}`;
      }
    });

    // Tự động làm mới danh sách voice khi hệ thống nạp xong
    if (window.speechSynthesis && window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.addEventListener('voiceschanged', () => this.populateVoices());
    }
  }

  setPlayingState(isPlaying) {
    if (this.iconPlay && this.iconPause) {
      this.iconPlay.style.display = isPlaying ? 'none' : 'block';
      this.iconPause.style.display = isPlaying ? 'block' : 'none';
    }
    const indicator = document.getElementById('tts-speaker-indicator');
    if (indicator) {
      if (isPlaying) indicator.classList.add('pulse');
      else indicator.classList.remove('pulse');
    }
  }

  show() {
    if (this.containerEl) {
      this.containerEl.style.display = 'block';
      this.expand();
      this.restorePosition();
    }
  }

  hide() {
    if (this.containerEl) {
      this.containerEl.style.display = 'none';
      this.clearAutoMinimizeTimer();
    }
  }

  toggle() {
    if (this.containerEl) {
      if (this.containerEl.style.display === 'none') {
        this.show();
        this.tts.play();
      } else {
        this.tts.stop();
        this.hide();
      }
    }
  }
}
